<template>
  <div v-if="!error" class="grid grid-cols-3 lg:grid-cols-5 gap-1">
    <button
      v-for="(image, index) in imagesArray"
      :id="`yotpo-customer-photo-${index}`"
      :key="image.id"
      class="relative cursor-pointer aspect-square group"
      type="button"
      :aria-label="`Open photo ${index + 1}`"
      @click="openCarousel(index)"
    >
      <img
        :src="image.image_url"
        :alt="`Customer photo ${index + 1} of ${image.product_name || 'Product'}`"
        loading="lazy"
        width="150"
        height="150"
        class="w-full h-auto object-cover"
      />

      <!-- Hover Overlay -->
      <div
        class="absolute inset-0 bg-white bg-opacity-50 opacity-0 hover:opacity-50 group-focus-visible:opacity-50 transition-opacity duration-200 flex items-center justify-center p-2"
      >
        <Stars
          v-if="image.score && image.reviews_count"
          :average-rating="image.score"
          :total-reviews="image.reviews_count"
          :show-average-rating="false"
          :show-total-reviews="false"
        />
      </div>
    </button>
  </div>

  <Overlay
    unique-key="yotpo-customer-photos-overlay"
    panel-width="FULL"
    panel-close-button="WHITE"
    modal-width="EXTRA_WIDE"
    mode="BLACK"
  >
    <template v-if="currentImage" #noPadding>
      <div class="w-full">
        <div class="flex flex-col lg:flex-row bg-white">
          <!-- Left Section: Large Customer Photo -->
          <div class="flex items-center w-full lg:w-1/2">
            <img
              :src="currentImage.medium_image_url"
              :alt="`Customer photo of ${currentImage.product_name || 'Product'}`"
              width="640"
              height="853"
              class="w-full h-auto object-contain"
            />
          </div>

          <!-- Right Section: Product and Review Details -->
          <div class="w-full lg:w-1/2 bg-white p-9 max-h-[640px] overflow-y-auto">
            <!-- Product Information - Loop over Tagged Products and show a thumb to toggle between them -->
            <div v-if="currentProduct" class="flex items-center justify-center gap-4 mb-6">
              <img
                :src="currentProduct.image_url"
                :alt="currentProduct.name"
                width="112"
                height="112"
                class="w-28 h-28 object-cover rounded"
              />
              <div v-if="currentProduct.score && currentProduct.reviews_count" class="flex-1">
                <div class="flex items-center gap-2 text-2xl font-bold">
                  {{ Math.round(currentProduct.score * 10) / 10 }}
                  <Stars
                    :average-rating="currentProduct.score"
                    :total-reviews="currentProduct.reviews_count"
                    :show-average-rating="false"
                    :show-total-reviews="false"
                  />
                </div>
                <div class="text-sm font-bold mb-3">{{ currentProduct.name }}</div>
                <BaseButton :to="currentProduct.product_url" variant="primary" size="SMALL" class="w-full lg:w-auto"
                  >Buy Now</BaseButton
                >
              </div>
            </div>

            <div v-if="currentImage.tagged_products.length > 1">
              <span class="text-sm font-bold mb-3 text-gray">Other tagged products</span>
              <div class="flex gap-4 mb-6">
                <button
                  v-for="(thumb, index) in currentImage.tagged_products"
                  :key="thumb.id"
                  type="button"
                  :aria-label="`Show product ${thumb.name}`"
                  @click="() => (selectedProductIndex = index)"
                >
                  <img
                    :src="thumb.image_url"
                    :alt="thumb.name || 'Product image'"
                    width="72"
                    height="72"
                    class="w-18 h-18 object-cover rounded"
                  />
                </button>
              </div>
            </div>

            <!-- Reviewer/Post information-->
            <div class="border-t border-gray-lighter lg:border-none flex items-center gap-3 mb-4 pt-6">
              <div class="flex-1">
                <div class="font-bold">{{ currentImage.comment.name }}</div>
                <div v-if="currentImage.comment.verified_buyer" class="flex items-center gap-1 text-sm text-gray">
                  Verified Buyer
                </div>
              </div>
              <div v-if="currentImage.comment.created_at" class="text-sm text-gray">
                {{ dateToLocaleEnUS(currentImage.comment.created_at) }}
              </div>
            </div>

            <!-- Content -->
            <div class="flex-1">
              <Stars
                v-if="currentImage.comment.score && currentImage.comment.reviews_count"
                :average-rating="currentImage.comment.score"
                :total-reviews="currentImage.comment.reviews_count"
                :show-average-rating="false"
                :show-total-reviews="false"
              />
              <div v-if="currentImage.comment.title" class="font-bold my-2">{{ currentImage.comment.title }}</div>
              <p class="text-sm mb-4 break-words">{{ currentImage.comment.content }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Overlay>
</template>

<script setup lang="ts">
const { $sitewideConfig, $overlay, $device } = useNuxtApp()
const error = ref(false)
const images = ref<ImageRecord[]>([])
const selectedImageIndex = ref(0)
const selectedProductIndex = ref(0)

const { yotpoId } = defineProps<{
  yotpoId: string
}>()

onMounted(() => {
  getImages()
})

const imagesArray = computed(() => {
  if (!images.value) return []
  // Cap images to 10 for desktop and 6 for mobile
  const maxImages = $device.value.isMobile ? 6 : 10
  return images.value.slice(0, maxImages)
})

const currentImage = computed(() => images.value[selectedImageIndex.value])
const currentProduct = computed(() => currentImage.value.tagged_products[selectedProductIndex.value])

async function getImages() {
  try {
    const url = `https://api.yotpo.com/v1/widget/${$sitewideConfig.config.yotpoApiKey}/albums/product/${yotpoId}?page=1&per_page=10`

    const res = await $fetch<YotpoCustomerPhotosResponse>(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })

    images.value = res.response.images.map((image) => {
      const createdTime = image.review?.created_at || image.post?.created_time
      const userName = image.review?.reviewer_display_name || image.post?.username || ''
      // remove any image.tagged_products that do not have an image_url
      const taggedProducts = image.tagged_products.filter((product) => product.image_url)
      return {
        id: image.image_id,
        image_url: image.image_url,
        medium_image_url: image.medium_image_url,
        product_name: image.product?.name || '',
        product_url: image.product?.product_url || '',
        score: image.product?.score || undefined,
        reviews_count: image.product?.reviews_count || undefined,
        tagged_products: taggedProducts,
        comment: {
          name: userName,
          verified_buyer: image.review?.verified_buyer || false,
          created_at: createdTime,
          score: image.review?.score || undefined,
          reviews_count: image.product?.reviews_count || undefined,
          title: image.review?.title || '',
          content: image.review?.content || image.post?.content,
        },
      }
    })
  } catch (err) {
    error.value = true
  }
}

function openCarousel(index: number) {
  $overlay.open(`yotpo-customer-photos-overlay`, () =>
    document.getElementById(`yotpo-customer-photo-${index}`)?.focus()
  )
  selectedImageIndex.value = index
}

interface ImageRecord {
  id: string
  image_url: string
  medium_image_url: string
  product_name: string
  product_url: string
  score?: number
  reviews_count?: number
  tagged_products: YotpoTaggedProduct[]
  comment: {
    name: string
    verified_buyer: boolean
    image_url?: string
    created_at?: string
    score?: number
    reviews_count?: number
    title?: string
    content?: string
  }
}
</script>
